import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    Input,
    OnChanges,
    SimpleChanges,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { ICustomerDetails, IWorkItem } from '@dxp/core/models';
import { tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { EndCustomerDetailsComponent } from './customer-details/customer-details.component';
import { EndCustomerDirectoryComponent } from './customer-directory/customer-directory.component';
import {
    EndCustomerStateService,
    ViewType,
} from './services/end-customer.state.service';
import { CustomerDetailsService } from './customer-details/customer-details.service';

@Component({
    selector: 'end-customer-panel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        EndCustomerDetailsComponent,
        EndCustomerDirectoryComponent,
    ],
    providers: [EndCustomerStateService, CustomerDetailsService],
    templateUrl: './end-customer.component.html',
    styleUrl: './end-customer.component.scss',
})
export class EndCustomerComponent implements OnChanges {
    readonly #destoryRef = inject(DestroyRef);
    readonly #endCustomerStateService = inject(EndCustomerStateService);

    @Input() workItem?: IWorkItem;
    @Input() customer?: string;
    allowNavigation = input.required<boolean>();
    updated = output<ICustomerDetails>();

    activeEndCustomer = computed<ICustomerDetails | undefined>(() =>
        this.#endCustomerStateService.activeEndCustomer(),
    );

    readonly view = computed<ViewType>(() =>
        this.#endCustomerStateService.view(),
    );
    readonly loaded = signal<boolean>(false);

    ngOnChanges(changes: SimpleChanges) {
        if (changes['workItem']) {
            const { currentValue, previousValue } = changes['workItem'];

            const current = currentValue as IWorkItem | null | undefined;
            const previous = previousValue as IWorkItem | null | undefined;

            if (
                current &&
                (!previous || current.workItemId !== previous.workItemId)
            ) {
                this.#initialiseEndCustomerViewFromWorkItem(current);
            }
        }

        if (changes['customer']) {
            const { currentValue, previousValue } = changes['customer'];

            const current = currentValue as string | null | undefined;
            const previous = previousValue as string | null | undefined;

            if (current && (!previous || current !== previous)) {
                this.#initialiseEndCustomerViewFromCustomer(current);
            }
        }
    }

    #initialiseEndCustomerViewFromWorkItem(workItem: IWorkItem) {
        this.#endCustomerStateService.setView('details');
        this.#endCustomerStateService.setBusinessUnit(workItem.businessUnitId);
        this.#endCustomerStateService.setConversation(
            workItem.primaryConversationId,
        );
        this.#endCustomerStateService.setMediaType(workItem.primaryMediaType);

        if (workItem.customerId) {
            this.#endCustomerStateService
                .setActiveEndCustomer(workItem.customerId)
                .pipe(
                    tap(() => {
                        this.#endCustomerStateService.setSelectedEndCustomer(
                            this.#endCustomerStateService.activeEndCustomer(),
                        );

                        this.loaded.set(true);
                    }),
                    takeUntilDestroyed(this.#destoryRef),
                )
                .subscribe();
        } else {
            this.loaded.set(true);
        }
    }

    #initialiseEndCustomerViewFromCustomer(customerId: string) {
        this.#endCustomerStateService.setView('details');

        this.#endCustomerStateService
            .setActiveEndCustomer(customerId)
            .pipe(
                tap(customer => {
                    this.#endCustomerStateService.setSelectedEndCustomer(
                        this.#endCustomerStateService.activeEndCustomer(),
                    );

                    this.#endCustomerStateService.setBusinessUnit(
                        customer.businessUnitId,
                    );

                    this.loaded.set(true);
                }),
                takeUntilDestroyed(this.#destoryRef),
            )
            .subscribe();
    }

    onUpdated(customer: ICustomerDetails) {
        this.updated.emit(customer);
    }
}
